import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import _ from "lodash";
import intl from "../../i18n/intl";

const initialState = {
  enterprise: null,
  requesting: false,
  error: null,
  action: null,
};

const customIntl = intl();

const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    resetState: (state, action) => {
      state.requesting = false;
      state.error = null;
      state.action = null;
      state.enterprise = null;
    },
    requestEnterprise: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.enterprise = action.payload.refresh ? null : state.enterprise;
      state.action = "details";
    },
    setEnterpriseSuccess: (state, action) => {
      state.enterprise = {
        ...state.enterprise,
        ...action?.payload?.response,
      };
      state.requesting = false;
      state.error = null;
    },
    setEnterpriseFail: (state, action) => {
      state.enterprise = null;
      state.requesting = false;
      state.error = action.payload.response;
    },
    resetEnterprise: (state, action) => {
      state.enterprise = null;
    },
    updateSelfEnterpriseRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "update";
    },
    updateSelfEnterpriseSuccess: (state, action) => {
      state.enterprise = {
        ...state.enterprise,
        ...(action?.payload?.response || null),
      };
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          { id: "action_success" },
          {
            action: customIntl.formatMessage({ id: "update_performed" }),
            object: customIntl.formatMessage({ id: "enterprise" }),
          }
        )
      );
    },
    updateSelfEnterpriseFail: (state, action) => {
      state.enterprise = null;
      state.requesting = false;
      state.error = action.payload.response;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    enterpriseSMTPDetailsRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "enterprise_smtp_details";
    },
    enterpriseSMTPDetailsSuccess: (state, action) => {
      state.enterprise = {
        ...state.enterprise,
        smtp: action.payload.response,
      };
      state.requesting = false;
      state.error = null;
    },
    enterpriseSMTPDetailsFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    updateEnterpriseSMTPRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "update_smtp";
    },
    updateEnterpriseSMTPSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          { id: "action_success" },
          {
            action: customIntl.formatMessage({ id: "update_performed" }),
            object: customIntl.formatMessage({ id: "smtp_settings" }),
          }
        )
      );
    },
    updateEnterpriseSMTPFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    emailBannerDetailsRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "email_banner_details";
    },
    emailBannerDetailsSuccess: (state, action) => {
      state.enterprise = {
        ...state.enterprise,
        emailSettings: {
          ...state.enterprise.emailSettings,
          banners: action.payload.response,
        },
      };
      state.requesting = false;
      state.error = null;
    },
    emailBannerDetailsFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    updateEmailBannerRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "update_email_banner";
    },
    updateEmailBannerSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          { id: "action_success" },
          {
            action: customIntl.formatMessage({ id: "update_performed" }),
            object: customIntl.formatMessage({ id: "email_settings" }),
          }
        )
      );
    },
    updateEmailBannerFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    learnerConfigurationRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "learner_configuration";
    },
    learnerConfigurationSuccess: (state, action) => {
      state.enterprise = {
        ...state.enterprise,
        learningConfiguration: action.payload.response,
      };
      state.requesting = false;
      state.error = null;
    },
    learnerConfigurationFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    updateLearnerConfigurationRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "update_learner_configuration";
    },
    updateLearnerConfigurationSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          { id: "action_success" },
          {
            action: customIntl.formatMessage({ id: "update_performed" }),
            object: customIntl.formatMessage({ id: "learning_configurations" }),
          }
        )
      );
    },
    updateLearnerConfigurationFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    languageConfigurationRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "language_configuration";
    },
    languageConfigurationSuccess: (state, action) => {
      state.enterprise = {
        ...state.enterprise,
        languageConfiguration: {
          ...state.enterprise.languageConfiguration,
          ...action.payload.response,
          allowedLanguages: _.uniq([
            ...action.payload.response.allowedLanguages,
            "en",
          ]),
        },
      };
      state.requesting = false;
      state.error = null;
    },
    languageConfigurationFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    updateLanguageConfigurationRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "update_language_configuration";
    },
    updateLanguageConfigurationSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          { id: "action_success" },
          {
            action: customIntl.formatMessage({ id: "update_performed" }),
            object: customIntl.formatMessage({ id: "language_configurations" }),
          }
        )
      );
    },
    updateLanguageConfigurationFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
  },
});

export const {
  emailBannerDetailsFail,
  emailBannerDetailsRequest,
  emailBannerDetailsSuccess,
  enterpriseSMTPDetailsFail,
  enterpriseSMTPDetailsRequest,
  enterpriseSMTPDetailsSuccess,
  requestEnterprise,
  resetEnterprise,
  resetState,
  setEnterpriseFail,
  setEnterpriseSuccess,
  updateEmailBannerFail,
  updateEmailBannerRequest,
  updateEmailBannerSuccess,
  updateEnterpriseSMTPFail,
  updateEnterpriseSMTPRequest,
  updateEnterpriseSMTPSuccess,
  updateSelfEnterpriseFail,
  updateSelfEnterpriseRequest,
  updateSelfEnterpriseSuccess,
  learnerConfigurationFail,
  learnerConfigurationRequest,
  learnerConfigurationSuccess,
  updateLearnerConfigurationFail,
  updateLearnerConfigurationRequest,
  updateLearnerConfigurationSuccess,
  languageConfigurationFail,
  languageConfigurationRequest,
  languageConfigurationSuccess,
  updateLanguageConfigurationFail,
  updateLanguageConfigurationRequest,
  updateLanguageConfigurationSuccess,
} = globalSlice.actions;
export default globalSlice.reducer;
